import Backbone from 'backbone'
import _ from 'lodash'
import $ from 'jquery'
import calendars from 'scoreboard/components/calendars'
import breakdownPeriodOptions from 'scoreboard/components/designer/breakdown-period-options'
import dropdown from 'scoreboard/components/dropdown'
import i18n from 'scoreboard/components/i18n'
import RelativeOptions from 'scoreboard/components/relative-options'
import unwrapModel from 'scoreboard/components/unwrap-model'
import breakdownRangeLabels from 'scoreboard/charts/breakdown-range-labels'

/**
 * @typedef ChartSortByValueRange see ChartSortByValueRange.kt
 *
 * @property {String} [datasetValue]
 * @property {Int} [datasetCatOrRangeId]
 * @property {Int} [periodId]
 * @property {Int} [periodDelta]
 * @property {Int} [periodIndex]
 */

const SortByValueRangeSelect = Backbone.View.extend({
	tagname: 'span',

	events: {
		'change': 'onChange'
	},

	/**
	 * @constructs
	 *
	 * @param {Object} options
	 *
	 * @param {ChartDesigner} chart
	 *
	 * @param {ChartDesignerBreakdown} breakdown - breakdown from which we're
	 * picking a range value
	 *
	 * @param {ChartSortByValueRange} sortByValueRange - the initial range to
	 * be selected. Will be mutated when a new option is selected
	 *
	 * @param {String} name - the "name" property to use for the <select>
	 */
	initialize: function(options = {}) {
		this.options = options
		this.breakdown = options.breakdown
		this.sortByValueRange = options.sortByValueRange
		this.chart = options.chart
	},

	render: function() {
		this.select = $(`<select name="${this.options.name}">`).appendTo(this.el)

		let initialValue = toSelectId({
			id: this.sortByValueRange.rangeId,
			tempId: this.sortByValueRange.rangeTempId
		}, _.pick(this.sortByValueRange, 'periodId', 'periodDelta', 'periodIndex'))

		dropdown.idLabels(this.select, this.selectOptions(), initialValue)
	},

	selectOptions: function () {
		let breakdownBy = this.breakdown.get('breakdownBy')
		let options
		switch (breakdownBy) {
			case 'every-value':
			case 'specific-values':
			case 'every-range':
			case 'specific-ranges':
				options = this.datasetBreakdownOptions(); break
			case 'calendar-period-ranges':
				options = this.calendarPeriodOptions(); break
			case 'calendar-period-grouped-ranges':
				options = this.groupedCalendarPeriodOptions(); break
			default: throw Error('not yet supported: ' + breakdownBy)
		}

		options.unshift({
			id: '',
			label: i18n.get('charts.series.all')
		})

		return options
	},

	datasetBreakdownOptions: function () {
		return this.breakdown.ranges
			.map(range => {
				return {
					id: toSelectId(range),
					// Note: breakdownRangeLabels() takes care of special options
					// like "Other" and "Blank"
					label: breakdownRangeLabels({
						data: this.chart.data,
						breakdown: this.breakdown,
						range
					}).label
				}
			})
	},

	calendarPeriodOptions: function () {
		return _.flatMap(this.breakdown.ranges.toArray(), range => {
			if (!_.isNil(range.get('startPeriodId'))) {
				return this.absoluteCalendarPeriodOptions(range)
			} else {
				return this.relativeCalendarPeriodOptions(range)
			}
		})
	},

	absoluteCalendarPeriodOptions: function (range) {
		return calendars.periodSublist(range.get('startPeriodId'), range.get('endPeriodId'))
			.map(p => {
				return {
					id: toSelectId(range, { periodId: p.id }),
					label: p.label
				}
			})
	},

	relativeCalendarPeriodOptions: function (range) {
		return new RelativeOptions({
			start: range.get('startPeriodDelta'),
			end: range.get('endPeriodDelta')
		}).generate().map(opt => {
			return {
				id: toSelectId(range, { periodDelta: opt.id }),
				label: opt.label
			}
		})
	},

	groupedCalendarPeriodOptions: function () {
		return _.flatMap(this.breakdown.ranges.toArray(), range => {
			if (!_.isNil(range.get('startPeriodIndex'))) {
				return this.absoluteGroupedPeriodOptions(range)
			} else {
				return this.relativeCalendarPeriodOptions(range)
			}
		})
	},

	absoluteGroupedPeriodOptions: function (range) {
		return breakdownPeriodOptions({
			groupCalendarId: range.get('groupCalendarId'),
			breakdownCalendarId: range.get('breakdownCalendarId')
		}).map(opt => {
			return {
				id: toSelectId(range, { periodIndex: opt.id }),
				label: opt.label
			}
		})
	},

	onChange: function () {
		// Start out with all relevant ChartSortByValueRange properties set
		// to null, since only one can have a value
		let update = {
			rangeId: null,
			rangeTempId: null,
			periodId: null,
			periodDelta: null,
			periodIndex: null
		}

		let selectVal = fromSelectId(this.select.val())

		if (selectVal) {
			let range = this.breakdown.ranges.findByIds(selectVal)
			_.extend(update, {
				rangeId: range.get('id'),
				rangeTempId: range.get('tempId')
			}, _.pick(selectVal, 'periodId', 'periodDelta', 'periodIndex'))
		}

		_.extend(this.sortByValueRange, update)
		this.trigger('change', this.sortByValueRange)
	}
})

/**
 * @typedef {Object} PeriodInfo
 *
 * @property {Int} [periodId] - for absolute ranges, the absolute period ID
 * @property {Int} [periodDelta] - for relative ranges, the period delta
 * @property {Int} [periodIndex] - for grouped absolute ranges, the period index
 */

/**
 * For the given breakdown range, returns an ID to use in an HTML select. Makes
 * sure it is both unique and reversible (so we can determine which range was
 * selected).
 *
 * @param {ChartDesignerRange} range
 * @param {PeriodInfo} periodInfo
 * @returns {String}
 */
function toSelectId (range, periodInfo = {}) {
	if (!range) {
		return ''
	}

	const {id, tempId} = unwrapModel(range)
	let parts = [!!tempId ? `tempId=${tempId}` : `id=${id}`]

	if (!_.isNil(periodInfo.periodId)) {
		parts.push('periodId=' + periodInfo.periodId)
	} else if (!_.isNil(periodInfo.periodDelta)) {
		parts.push('periodDelta=' + periodInfo.periodDelta)
	} else if (!_.isNil(periodInfo.periodIndex)) {
		parts.push('periodIndex=' + periodInfo.periodIndex)
	}

	return parts.join(',')
}

/**
 * @typedef {Object} RangeAndPeriod
 *
 * @property {Int} [id] - for persisted ranges, the range's ID
 * @property {String} [tempId] - for ranges that haven't been persisted, the range's tempId
 * @property {Int} [periodId] - for absolute ranges, the absolute period ID
 * @property {Int} [periodDelta] - for relative ranges, the period delta
 * @property {Int} [periodIndex] - for absolute grouped ranges, the period index
 */

/**
 * For the given ID from the range HTML select, returns the range that was selected.
 * May also have period id/delta for calendar period ranges.
 *
 * @param {String} id
 * @returns {RangeAndPeriod} range/period info, or null if All was selected
 */
function fromSelectId (id) {
	if (!id) {
		return null
	}

	let rangeAndPeriod = {}
	let parts = id.split(',')

	if (parts.length > 0) {
		let rangePart = parts[0]
		if (rangePart.startsWith('id=')) {
			rangeAndPeriod.id = parseInt(rangePart.replace('id=', ''))
		} else if (rangePart.startsWith('tempId=')) {
			rangeAndPeriod.tempId = rangePart.replace('tempId=', '')
		}
	}

	if (parts.length > 1) {
		let periodPart = parts[1]
		let [attr, value] = periodPart.split('=')

		rangeAndPeriod[attr] = parseInt(value)
	}

	return rangeAndPeriod
}

export default SortByValueRangeSelect

SortByValueRangeSelect.__filename__ = 'charts/edit/sort/sort-by-value-range-select.js'
